import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { flatMap, map, switchMap, takeUntil } from 'rxjs/operators';
import { AircraftHttpClient } from 'src/app/shared/flight-log-api/aircraft/aircraft-client.service';
import {
  EngineMaintenanceDetailDto,
  MaintenanceSummaryDto,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import {
  ConfigurationClassDto,
  MaintenanceParameterValueDto,
  Snapshot,
} from 'src/app/shared/flight-log-api/maintenance/maintenance-client.model';
import { MaintenanceHttpClient } from 'src/app/shared/flight-log-api/maintenance/maintenance-client.service';
import { AircraftEntity } from 'src/store/aircraft/aircraft.model';
import { filterNull } from 'src/util/rxjs/operators';
import { humanize } from 'src/util/text';

function toDisplayValue(text: string) {
  text = humanize(text, false);
  text = text.split(' ').reduce((cur, next) => {
    cur = cur == null || cur === '' ? '' : cur[0].toUpperCase() + cur.substr(1);
    next = next == null || next === '' ? '' : next[0].toUpperCase() + next.substr(1);
    return next !== '' ? cur + ' ' + next : cur;
  });

  return text;
}

export interface MaintenanceComparisonValue {
  text: string;
  paramName: string;
  engineNumber?: number;
  ramcoValue: number;
  flightLogValue: number | null;
}

function getComparisonValues(
  flightLog: MaintenanceSummaryDto,
  ramco: MaintenanceParameterValueDto[],
  config: ConfigurationClassDto,
): MaintenanceComparisonValue[] {
  const getRamcoParamValue = (param: string, engine?: number) => {
    const rp = ramco.find(
      (p) =>
        p.name === param &&
        ((engine == null && p.engineNumber == null) || engine === p.engineNumber),
    );
    return rp != null && rp.value != null ? rp.value : 0;
  };

  function getEngineComparisonValues(
    engine: EngineMaintenanceDetailDto,
    engineNumber: number,
  ): MaintenanceComparisonValue[] {
    // map engine parameters to their Flight Log values
    const engineParamMapping: [
      keyof EngineMaintenanceDetailDto | keyof EngineMaintenanceDetailDto['cycles'],
      number | null,
    ][] = [
      // standard engine parameters and their Flight Log value
      ['starts', engine.starts],
      ['time', engine.time],
      // engine parameters defined by the aircraft's configuration class and their Flight Log value
      ...config.maintenanceParameters.map<[string, number]>((configClassParam) => [
        configClassParam,
        engine.cycles[configClassParam],
      ]),
    ];
    // Create comparison model for each of our engine parameters, also getting its Ramco value
    return engineParamMapping.map(([paramName, flightLogValue]) => {
      return {
        text: `Engine ${engineNumber} ${toDisplayValue(String(paramName))}`,
        paramName: String(paramName),
        ramcoValue: getRamcoParamValue(String(paramName), engineNumber),
        // tslint:disable-next-line: no-non-null-assertion
        flightLogValue: flightLogValue!,
        engineNumber,
      };
    });
  }

  // standard airframe parameters and their Flight Log  values
  const airframeValueMap: [keyof MaintenanceSummaryDto, string | null, number | null][] = [
    ['totalFlightTime', 'time', flightLog.airframeTime],
    ['runOnLandings', null, flightLog.runOnLandings],
    ['totalLandings', null, flightLog.totalLandings],
  ];

  const nonRamcoAirframeValueMap: [string, number | null][] = [
    ['hobbs', flightLog.totalFlightTime],
  ];

  let values: MaintenanceComparisonValue[] = [
    ...nonRamcoAirframeValueMap.map(([paramName, flightLogValue]) => {
      return {
        text: toDisplayValue(paramName),
        paramName,
        ramcoValue: 0,
        flightLogValue,
      };
    }),
    // Create comparison model for each of our airframe parameters, also getting its Ramco value
    ...airframeValueMap.map(([paramName, alternateParamName, flightLogValue]) => {
      const ramcoValue = getRamcoParamValue(paramName);
      return {
        text: toDisplayValue(paramName),
        paramName,
        ramcoValue:
          ramcoValue === 0 && alternateParamName != null
            ? getRamcoParamValue(alternateParamName)
            : ramcoValue,
        flightLogValue,
      };
    }),
    // engine 1
    ...getEngineComparisonValues(flightLog.engine1, 1),
  ];

  if (config.engineCount > 1) {
    values = [
      ...values,
      // engine 2
      ...getEngineComparisonValues(flightLog.engine2, 2),
    ];
  }

  return values;
}

@Component({
  selector: 'fl-maintenance-comparison-page',
  templateUrl: './maintenance-comparison-page.component.html',
  styleUrls: ['./maintenance-comparison-page.component.scss'],
})
export class MaintenanceComparisonPageComponent implements OnInit, OnDestroy {
  public aircraft$ = new BehaviorSubject<AircraftEntity | undefined>(undefined);
  public snapshots$ = new BehaviorSubject<Snapshot[] | null>(null);
  public values$ = new BehaviorSubject<MaintenanceComparisonValue[] | null>(null);
  public refresh$ = new BehaviorSubject<any>(null);

  public selectedAircraftIdCtrl = new FormControl();

  private _onDestroy$ = new Subject();

  constructor(
    private _route: ActivatedRoute,
    private _aircraft: AircraftHttpClient,
    private _maintenance: MaintenanceHttpClient,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _location: Location,
  ) {}

  public ngOnInit() {
    this.selectedAircraftIdCtrl.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe((id) => {
      if (id == null) {
        this.aircraft$.next(undefined);
        this.values$.next(null);
        this.snapshots$.next(null);
      }
      const tree = this._router.createUrlTree([`/maintenance/parameters/${id == null ? '' : id}`]);
      this._location.replaceState(this._router.serializeUrl(tree));
    });

    this.selectedAircraftIdCtrl.valueChanges
      .pipe(
        map((aircraftId) => aircraftId as string | null),
        filterNull(),
        switchMap((aircraftId) => this._aircraft.getById(aircraftId)),
        takeUntil(this._onDestroy$),
      )
      .subscribe(this.aircraft$);

    combineLatest([this.aircraft$, this.refresh$])
      .pipe(
        map(([aircraft]) => aircraft),
        filterNull(),
        switchMap((aircraft) => this._maintenance.getSnapshots(aircraft.id)),
        takeUntil(this._onDestroy$),
      )
      .subscribe(this.snapshots$);

    this.aircraft$
      .pipe(
        filterNull(),
        flatMap(async (aircraft: AircraftEntity) => {
          if (aircraft.configurationClassId == null) {
            return null;
          }
          let flightLog: MaintenanceSummaryDto = {
            runOnLandings: 0,
            totalFlightTime: 0,
            totalLandings: 0,
            startingHobbsReading: 0,
            endingHobbsReading: 0,
            engine1: { starts: 0, time: 0, cycles: {} },
            engine2: { starts: 0, time: 0, cycles: {} },
            airframeTime: 0,
            hook: { cycles: 0, flightTime: 0, nightTime: 0 },
            hoist: { cycles: 0, flightTime: 0, nightTime: 0 },
          };

          try {
            flightLog = await this._maintenance
              .getFlightLogMaintenanceParameterTotals(aircraft.id)
              .toPromise();
          } catch (err) {
            // no totals available
          }

          const ramco = await this._maintenance
            .getRamcoMaintenanceParameterTotals(aircraft.id)
            .toPromise();

          const configClass = await this._maintenance
            .getConfigurationClassById(aircraft.configurationClassId)
            .toPromise();

          const values = getComparisonValues(flightLog, ramco, configClass);

          return values;
        }),
        takeUntil(this._onDestroy$),
      )
      .subscribe(this.values$);

    this.selectedAircraftIdCtrl.patchValue(this._route.snapshot.paramMap.get('aircraftId'));
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public async createSnapshot({
    values,
    remarks,
    snapshotEffectiveDate,
  }: {
    remarks: string;
    values: MaintenanceParameterValueDto[];
    snapshotEffectiveDate: Date;
  }) {
    const options = { duration: 3000 };
    this._snackBar.open('Creating snapshot...', undefined, options);
    try {
      await this._maintenance
        .snapshotMaintenanceParameters({
          aircraftId: this.selectedAircraftIdCtrl.value,
          remarks,
          values,
          snapshotEffectiveDate,
        })
        .toPromise();
      this._snackBar.open('Snapshot created.', undefined, options);
      this.refresh$.next(null);
    } catch (err) {
      this._snackBar.open('Error creating snapshot.', undefined, options);
      throw err;
    }
  }
}
